import { currentQuarter } from './model';
import type { LiveOptions, MetricRow, OfferRow, SourcePopulation } from './sourceTypes';

/** Workbook dashboards ported natively; the spec's report_view filter takes exactly one of these per request. */
export const REPORT_VIEWS = ['PCM', 'MM', 'Experience', 'Agent Hub'] as const;
export type ReportView = (typeof REPORT_VIEWS)[number];
/** Reads a view from the URL. Exact values win; 'agent-hub', 'agent_hub' or other casing also resolve. Unknown → PCM. */
export function parseReportView(value: string | null | undefined): ReportView {
  const normal = (v: string) => v.toLowerCase().replace(/[\s_-]+/g, '');
  return REPORT_VIEWS.find((view) => value === view || normal(view) === normal(value ?? '')) ?? 'PCM';
}
/** MM and Agent Hub Targets sheets lead with the person (no RSM region column); PCM and Experience lead with RSM region. */
export const personFirst = (view: string) => view === 'MM' || view === 'Agent Hub';
export const VIEW_TITLES: Record<ReportView, string> = {
  PCM: 'PCM Tracker',
  MM: 'Market Manager Tracker',
  Experience: 'Experience PCM Tracker',
  'Agent Hub': 'Agent Hub BDM Tracker',
};
/** Header formula text from each workbook dashboard title. */
export const VIEW_FORMULAS: Record<ReportView, string> = {
  PCM: 'Net Margin (Excl. Exp) = GM(AR) with override + Luxplus add back - Promo',
  MM: 'Net Margin (Excl. Exp) = GM(AR) with override + Luxplus add back - Promo',
  Experience: 'Net Margin (Experience) = GM(Experience) - ICS Cost Margin Reduction',
  'Agent Hub': 'Net Margin = GM Adjusted AR',
};
/** Cards each view reads. Agent Hub offers come from bdm_tracker_offers_ah and its total from agent_hub_team. */
export function cardsForView(view: string): Array<string> {
  return view === 'Agent Hub' ? ['targets', 'agent_hub_team', 'agent_hub_offers'] : ['targets', 'offers'];
}
/** Agent Hub BDM rows each repeat the full team net margin, so it is counted once and never summed (F-47). */
export const countedOnce = (view: string, metric: string) => view === 'Agent Hub' && metric === 'net_margin_adjusted';

export const COLOURS = { actual: '#0F464F', forecast: '#4EFEB9', target: '#0F0C08', yes: '#0F464F', no: '#0F0C08' };
export const METRICS = [
  ['net_margin_adjusted', 'Net Margin (Excl. Exp)', 'money'],
  ['calls', 'Calls / VC', 'number'],
  ['meetings', 'Meetings', 'number'],
  ['proposals', 'Proposals', 'number'],
  ['net_signings', 'Net Signings', 'number'],
  ['reopened_signings', 'Reopened Signings', 'number'],
  ['mpoi_actual', 'MpOI', 'ratio'],
  ['mpv_actual', 'MpV', 'ratio'],
  ['no_of_activated_ah_agents', 'Activated Agents', 'number'],
  ['activity_point', 'Total Activity', 'number'],
] as const;
export const METRIC_IDS: ReadonlyArray<string> = METRICS.map(([id]) => id);
/** Net margin is "Net Margin" on the Experience and Agent Hub sheets, "Net Margin (Excl. Exp)" on PCM and MM. */
export function metricLabel(id: string, view: string): string {
  if (id === 'net_margin_adjusted' && (view === 'Experience' || view === 'Agent Hub')) {
    return 'Net Margin';
  }
  return METRICS.find(([metric]) => metric === id)?.[1] ?? id;
}
/** Metric rows each workbook Targets sheet keeps (Targets, Targets - MM, Experience and Agent_Hub metric_name filters). */
export const VIEW_METRICS: Record<ReportView, ReadonlyArray<string>> = {
  PCM: ['net_margin_adjusted', 'net_signings', 'proposals', 'calls', 'meetings', 'reopened_signings', 'activity_point'],
  MM: [
    'net_margin_adjusted',
    'net_signings',
    'proposals',
    'calls',
    'meetings',
    'mpoi_actual',
    'mpv_actual',
    'no_of_activated_ah_agents',
    'activity_point',
  ],
  Experience: ['net_margin_adjusted', 'net_signings', 'proposals', 'activity_point'],
  'Agent Hub': ['net_margin_adjusted', 'no_of_activated_ah_agents', 'activity_point'],
};
/** KPI bullet charts on each workbook dashboard, in workbook order. */
export const VIEW_CHARTS: Record<ReportView, ReadonlyArray<string>> = {
  PCM: ['net_margin_adjusted', 'net_signings', 'calls', 'meetings', 'proposals'],
  MM: ['net_margin_adjusted', 'net_signings', 'calls', 'meetings', 'proposals'],
  Experience: ['net_margin_adjusted', 'activity_point'],
  'Agent Hub': ['no_of_activated_ah_agents', 'activity_point'],
};
/** Workbook Forecast keeps MpOI and MpV at their actual value instead of projecting them to quarter end. */
const UNPROJECTED_METRICS = ['mpoi_actual', 'mpv_actual'];
export type Filters = {
  quarter: string;
  manager: string;
  region: string;
  category: string;
  person: string;
  metric: string;
  refund: string;
  brand: string;
  product: string;
  country: string;
  offer: string;
  section: string;
  rsmRegion: string;
  opportunity: string;
  offerName: string;
  selection: string;
  bdm: string;
};
export const defaults = (now = new Date()): Filters => ({
  quarter: currentQuarter(now),
  manager: 'All',
  region: 'All',
  category: 'All',
  person: 'All',
  metric: 'net_margin_adjusted',
  refund: 'After Refund',
  brand: 'All',
  product: 'All',
  country: 'All',
  offer: 'All',
  section: 'Scorecard',
  rsmRegion: 'All',
  opportunity: 'All',
  offerName: 'All',
  selection: '[]',
  bdm: 'All',
});
export const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
export function sum<T>(rs: Array<T>, fn: (r: T) => number | null | undefined): number | null {
  const vs = rs.map(fn).filter(finite);
  return vs.length ? vs.reduce((a, b) => a + b, 0) : null;
}
export const ratio = (a: number | null, b: number | null) => (finite(a) && finite(b) && b !== 0 ? a / b : null);
export function format(n: number | null | undefined, unit = 'number') {
  return finite(n)
    ? new Intl.NumberFormat('en-AU', {
        style: unit === 'percent' ? 'percent' : 'decimal',
        // Money always shows cents (A$10,917,215.60, not A$10,917,215.6), like the workbook's #,##0.00.
        minimumFractionDigits: unit === 'money' ? 2 : 0,
        maximumFractionDigits: unit === 'percent' ? 1 : 2,
      }).format(n)
    : 'Unknown';
}
export function quarterLabel(q: string) {
  if (!validQuarter(q)) {
    return 'Invalid quarter · ' + q;
  }
  const d = new Date(q + 'T00:00:00Z');
  return `FY Q${((Math.floor(d.getUTCMonth() / 3) + 2) % 4) + 1} · ${q}`;
}
export function dimensionValues(v: string): Array<string> {
  if (v === 'All') {
    return [];
  }
  try {
    const a = JSON.parse(v);
    if (Array.isArray(a)) {
      return a.filter((x) => typeof x === 'string');
    }
  } catch {}
  return [v];
}
export function dimensionMatches(v: string, raw: string | null) {
  return v === 'All' || dimensionValues(v).includes(raw ?? 'Unknown');
}
export const key = (v: string | null) => v ?? 'Unknown';
export function progress(quarter: string, asOf: string) {
  if (!validQuarter(quarter)) {
    return { total: 0, elapsed: 0 };
  }
  const start = new Date(quarter + 'T00:00:00Z');
  const end = new Date(start);
  end.setUTCMonth(end.getUTCMonth() + 3);
  const today = new Date(asOf + 'T00:00:00Z');
  const total = (+end - +start) / 86400000;
  const elapsed = Math.max(0, Math.min(total, (+today - +start) / 86400000));
  return { total, elapsed };
}
/** Header "Q2 Quarter Progress 21.7%": elapsed share of the quarter's calendar days. */
export function quarterProgress(quarter: string, asOf: string): number | null {
  const { total, elapsed } = progress(quarter, asOf);
  return total > 0 ? elapsed / total : null;
}
function max<T>(rs: Array<T>, fn: (r: T) => number | null | undefined): number | null {
  const vs = rs.map(fn).filter(finite);
  return vs.length ? Math.max(...vs) : null;
}
/** Experience Net Margin = GM (Experience) on the chosen refund basis minus ICS cost margin reduction. */
export function experienceNetMargin(gmAdjusted: number | null, icsCost: number | null): number | null {
  return finite(gmAdjusted) && finite(icsCost) ? gmAdjusted - icsCost : null;
}
/** once: rows repeat one team value (Agent Hub net margin), so take it once instead of summing the copies. */
export function stats(rows: Array<MetricRow>, quarter: string, asOf: string, metric?: string, once = false) {
  const actual = once ? max(rows, (r) => r.actual_value) : sum(rows, (r) => r.actual_value),
    target = once ? max(rows, (r) => r.target_value) : sum(rows, (r) => r.target_value);
  const { total, elapsed } = progress(quarter, asOf);
  const forecast = UNPROJECTED_METRICS.includes(metric ?? '')
    ? actual
    : finite(actual) && elapsed > 0
      ? (actual / elapsed) * total
      : null;
  const actualRatio = rows.length && target === 0 ? 0 : ratio(actual, target),
    forecastRatio = rows.length && target === 0 ? 0 : ratio(forecast, target);
  return {
    actual,
    target,
    forecast,
    actualRatio,
    forecastRatio,
    onTrack:
      !rows.length || !finite(target)
        ? 'Unknown'
        : target === 0
          ? 'Yes'
          : !finite(forecast)
            ? 'Unknown'
            : forecast >= target
              ? 'Yes'
              : 'No',
    elapsed,
    total,
    rows: rows.length,
  };
}
export function readFilters(search: string): Filters {
  const f = defaults(),
    p = new URLSearchParams(search);
  for (const k of Object.keys(f) as Array<keyof Filters>) {
    if (p.has(k)) {
      f[k] = p.get(k)!;
    }
  }
  return f;
}
export function writeFilters(f: Filters, extra: Record<string, string> = {}) {
  const p = new URLSearchParams({ ...f, ...extra });
  history.replaceState(null, '', location.pathname + '?' + p);
}
export function build(s: SourcePopulation, f: Filters, view: string, today: string, options: LiveOptions) {
  const tuples = parseSelection(f.selection);
  // Global filters are already applied before warehouse aggregation. Only the
  // presentation's exact owner/region tuple selection is evaluated here.
  const common = (r: MetricRow | OfferRow) =>
    !tuples.length ||
    tuples.some(
      (t) =>
        t.person === r.pcm_name && t.region === r.pcm_region && (personFirst(view) || t.rsmRegion === r.rsm_region),
    );
  const metrics = s.metrics.filter(common);
  const team = view === 'Agent Hub' ? (s.team ?? []) : [];
  const teamRow = (id: string) => team.find((r) => r.metric_name === id);
  const teamStats = (id: string) => {
    const r = teamRow(id);
    return r
      ? stats(
          [
            {
              quarter_start_date: f.quarter,
              pcm_name: null,
              pcm_region: null,
              rsm_region: null,
              metric_name: id,
              actual_value: r.actual_value,
              target_value: r.target_value,
              override_value: null,
            },
          ],
          f.quarter,
          today,
          id,
        )
      : null;
  };
  const metricsById = METRICS.map(([id, , unit]) => ({
    id,
    label: metricLabel(id, view),
    unit,
    // Agent Hub totals come from agent_hub_team (net margin once, counts summed) unless BDM rows are selected.
    ...((!tuples.length && teamStats(id)) ||
      stats(
        metrics.filter((r) => r.metric_name === id),
        f.quarter,
        today,
        id,
        countedOnce(view, id),
      )),
  }));
  const current = metricsById.find((m) => m.id === f.metric) ?? metricsById[0];
  const grouped = new Map<string, Array<MetricRow>>();
  for (const r of metrics) {
    if (!(VIEW_METRICS[view as ReportView] ?? VIEW_METRICS.PCM).includes(r.metric_name)) {
      continue;
    }
    const k = JSON.stringify([personFirst(view) ? null : r.rsm_region, r.pcm_region, r.pcm_name, r.metric_name]);
    const g = grouped.get(k) ?? [];
    g.push(r);
    grouped.set(k, g);
  }
  const targetOrder = [
    'net_margin_adjusted',
    'net_signings',
    'proposals',
    'calls',
    'meetings',
    'reopened_signings',
    'mpoi_actual',
    'mpv_actual',
    'no_of_activated_ah_agents',
    'activity_point',
  ];
  const targetRows = [...grouped.entries()]
    .map(([id, rows]) => {
      const r = rows[0];
      const metric = METRICS.find((m) => m[0] === r.metric_name);
      return {
        total: false,
        id,
        person: r.pcm_name,
        region: r.pcm_region,
        rsmRegion: r.rsm_region,
        metric: r.metric_name,
        label: metricLabel(r.metric_name, view),
        unit: metric[2],
        tuple: { person: r.pcm_name, region: r.pcm_region, rsmRegion: r.rsm_region },
        override: sum(rows, (row) => row.override_value),
        ...stats(rows, f.quarter, today, r.metric_name, countedOnce(view, r.metric_name)),
      };
    })
    .sort((a, b) => {
      const ak = personFirst(view) ? [key(a.person), key(a.region)] : [key(a.rsmRegion), key(a.region), key(a.person)];
      const bk = personFirst(view) ? [key(b.person), key(b.region)] : [key(b.rsmRegion), key(b.region), key(b.person)];
      const order = [
        'AU, South Pacific & Hawaii',
        'US',
        'EU',
        'Market Manager Lead',
        'Indonesia',
        'Thailand',
        'Middle East, Maldives, Japan',
        'Lifestyle Brands & White Label',
        'Cruises',
        'AusPac (Australia & South Pacific)',
        'MEA (Middle East & Asia)',
        'Europe',
        'North America',
        'Tours',
      ];
      const rank = (r: string | null) => (r === null ? 999 : order.includes(r) ? order.indexOf(r) : 100);
      return (
        (view === 'PCM' ? rank(a.rsmRegion) - rank(b.rsmRegion) : 0) ||
        ak.join('\u0000').localeCompare(bk.join('\u0000')) ||
        targetOrder.indexOf(a.metric) - targetOrder.indexOf(b.metric)
      );
    });

  const names = [...new Set(metrics.map((r) => key(r.pcm_name)))].sort();
  const people = names.map((name) => ({
    name,
    ...stats(
      metrics.filter((r) => key(r.pcm_name) === name && r.metric_name === current.id),
      f.quarter,
      today,
      current.id,
      countedOnce(view, current.id),
    ),
  }));
  // Agent Hub team total rows: one per sheet metric, net margin taken once from agent_hub_team, never a sum of BDMs.
  const teamTotalRows = VIEW_METRICS['Agent Hub']
    .map((id) => ({ id, row: teamRow(id), computed: teamStats(id) }))
    .filter((t) => t.row && t.computed)
    .map(({ id, row, computed }) => ({
      total: true,
      id: 'team-' + id,
      person: 'Team total',
      region: id === 'net_margin_adjusted' ? 'Counted once' : `Sum of ${row.bdm_count ?? 'Unknown'} BDMs`,
      rsmRegion: null,
      metric: id,
      label: metricLabel(id, view),
      unit: METRICS.find((m) => m[0] === id)[2],
      tuple: { person: null, region: null, rsmRegion: null },
      override: null,
      ...computed,
    }));
  const teamNetMargin = teamRow('net_margin_adjusted');
  const agentHubWarning =
    teamNetMargin && teamNetMargin.bdm_value_variants !== 1
      ? `Agent Hub BDM net margin rows carry ${teamNetMargin.bdm_value_variants ?? 'Unknown'} different values across ${teamNetMargin.bdm_count ?? 'Unknown'} BDMs. The source is expected to repeat one team value on every BDM, so the team total may be wrong.`
      : null;
  // Offers - Agent Hub follows the Bdm Name Selection filter (server side), not the clicked target rows:
  // bdm_tracker_offers_ah often has no BDM on a row, so a click filter would empty the table.
  const agentHubOffers = s.agentHubOffers ?? [];
  const offers = s.offers.filter(common);
  const valid =
    selectionValid(f.selection) &&
    validQuarter(f.quarter) &&
    METRICS.some((m) => m[0] === f.metric) &&
    ['Before Refund', 'After Refund'].includes(f.refund) &&
    ['Scorecard', 'Offers'].includes(f.section);
  const ar = f.refund === 'After Refund';
  const frozenToday = today;
  const offerRows = offers.map((r, i) => ({
    ...r,
    key: String(i),
    refunds: r.refunds_adjusted_paired,
    forecastMargin:
      r.salesperson_margin_forecast !== null && /^-?\d+(\.\d+)?$/.test(r.salesperson_margin_forecast)
        ? Math.trunc(Number(r.salesperson_margin_forecast))
        : null,
    displayName: ['Tourbank', 'Cruisebank'].includes(r.product_name ?? '')
      ? key(r.offer_name)
      : key(r.offer_opportunity_name),
    gm: ar ? r.gm_ar : r.gm_br,
    ttv: ar ? r.ttv_ar : r.ttv_br,
    adjustedGm: ar ? r.gm_adjusted_ar : r.gm_adjusted_br,
    adjustedTtv: ar ? r.ttv_adjusted_ar : r.ttv_adjusted_br,
    experienceNetMargin: experienceNetMargin(ar ? r.gm_adjusted_ar : r.gm_adjusted_br, r.ics_cost_margin_reduction_aud),
    addback: ar ? r.lp_addback_ar_paired : r.lp_addback_br_paired,
    status:
      !r.offer_start_date || !r.offer_end_date
        ? 'Unknown'
        : frozenToday < r.offer_start_date
          ? 'Not live'
          : frozenToday >= r.offer_end_date
            ? 'Ended'
            : 'Live',
  }));
  const displayedOfferRows = groupDisplayedOffers(offerRows, view);
  return {
    valid,
    targetRows,
    teamTotalRows,
    agentHubWarning,
    agentHubOffers: agentHubOffers.map((r) => ({ ...r, gmAdjusted: ar ? r.gm_adjusted_ar : r.gm_adjusted_br })),
    displayedOfferRows,
    metrics: metricsById,
    current,
    people,
    offerRows,
    metricRows: metrics.length,
    offerTotals: {
      gm: sum(offerRows, (r) => r.gm),
      ttv: sum(offerRows, (r) => r.ttv),
      adjustedGm: sum(offerRows, (r) => r.adjustedGm),
      adjustedTtv: sum(offerRows, (r) => r.adjustedTtv),
      netMargin: sum(offerRows, (r) => r.net_margin_adjusted),
      addback: sum(offerRows, (r) => r.addback),
      experienceNetMargin: sum(offerRows, (r) => r.experienceNetMargin),
    },
    options,
  };
}
export type SelectionTuple = { person: string | null; region: string | null; rsmRegion: string | null };
export function selectionValid(value: string) {
  try {
    const a = JSON.parse(value);
    return (
      Array.isArray(a) &&
      a.every((t) => t && ['person', 'region', 'rsmRegion'].every((k) => typeof t[k] === 'string' || t[k] === null))
    );
  } catch {
    return false;
  }
}
export function parseSelection(value: string): Array<SelectionTuple> {
  try {
    const a = JSON.parse(value);
    return Array.isArray(a)
      ? a.filter((t) => t && ['person', 'region', 'rsmRegion'].every((k) => typeof t[k] === 'string' || t[k] === null))
      : [];
  } catch {
    return [];
  }
}
export function toggleTargetSelection(value: string, tuple: SelectionTuple, multi = false) {
  const a = parseSelection(value);
  const same = (t: SelectionTuple) =>
    t.person === tuple.person && t.region === tuple.region && t.rsmRegion === tuple.rsmRegion;
  return JSON.stringify(
    multi ? (a.some(same) ? a.filter((t) => !same(t)) : [...a, tuple]) : a.length === 1 && same(a[0]) ? [] : [tuple],
  );
}
export function productLabel(name: string | null) {
  return (
    (
      {
        'Lux Partner Property': 'LPP',
        'Limited Time Lux Partner': 'LTS',
        'Limited Time Lux Exclusive': 'Flash',
        'Lux Premium Collection': 'LPC',
        'Last Minute Hotel': 'Last Minute',
      } as Record<string, string>
    )[name ?? ''] ?? key(name)
  );
}

export type CalculatedOffer = OfferRow & {
  key: string;
  displayName: string;
  gm: number | null;
  ttv: number | null;
  adjustedGm: number | null;
  adjustedTtv: number | null;
  addback: number | null;
  experienceNetMargin: number | null;
  status: string;
};
export type DisplayedOfferRow = CalculatedOffer & {
  sourceEvidence: Array<Record<string, unknown>>;
  sourceRows: number;
  displayProduct: string;
  marginForecast: number | null;
  forecastMargin: number | null;
  refunds: number | null;
  identityConflicts: Array<string>;
};
export function offerMarginForecast(row: Pick<OfferRow, 'salesperson_margin_forecast'>): number | null {
  const v = row.salesperson_margin_forecast;
  if (v === null || v === undefined || v.trim() === '') {
    return null;
  }
  const n = Number(v);
  return finite(n) ? Math.trunc(n) : null;
}
export function offerDisplayKey(r: CalculatedOffer, view = 'PCM'): string {
  const opportunity = ['Tourbank', 'Cruisebank'].includes(r.product_name ?? '')
    ? r.offer_name
    : r.offer_opportunity_name;
  const product = r.product_name === null ? null : productLabel(r.product_name);
  const dims =
    view === 'MM'
      ? [r.pcm_name, r.pcm_region, r.offer_country_name, opportunity, product]
      : view === 'Experience'
        ? [r.pcm_name, r.offer_country_name, r.offer_opportunity_name, r.offer_name, r.product_name]
        : [r.pcm_name, r.offer_country_name, opportunity, r.status, product, offerMarginForecast(r)];
  return JSON.stringify([r.quarter_start_date, ...dims]);
}
/** Original offer-sheet visible dimensions. Nulls remain distinct from literal display labels. */
export function groupDisplayedOffers(rows: Array<CalculatedOffer>, view = 'PCM'): Array<DisplayedOfferRow> {
  const groups = new Map<string, Array<CalculatedOffer>>();
  for (const r of rows) {
    const k = offerDisplayKey(r, view);
    const g = groups.get(k) ?? [];
    g.push(r);
    groups.set(k, g);
  }
  const numeric = [
    'refunds_adjusted_paired',
    'lp_addback_br_paired',
    'lp_addback_ar_paired',
    'gm_ar',
    'gm_br',
    'gm_adjusted_ar',
    'gm_adjusted_br',
    'ttv_ar',
    'ttv_br',
    'ttv_adjusted_ar',
    'ttv_adjusted_br',
    'net_margin_adjusted',
    'promo_used_aud',
    'lp_margin_add_back_ar',
    'ics_cost_margin_reduction_aud',
    'pcm_tour_addback',
    'ten_percent_pcm_tour_margin_reduction',
    'lp_margin_loss_sale',
    'lp_margin_gain_sale',
    'lp_margin_loss_refund',
    'lp_margin_gain_refund',
    'gm',
    'ttv',
    'adjustedGm',
    'adjustedTtv',
    'addback',
    'experienceNetMargin',
  ] as const;
  const identities = [
    'pcm_name',
    'pcm_region',
    'rsm',
    'rsm_region',
    'category',
    'brand_name',
    'product_name',
    'offer_opportunity_name',
    'offer_bk',
    'offer_name',
    'offer_start_date',
    'offer_end_date',
    'offer_country_name',
    'salesperson_margin_forecast',
  ] as const;
  return [...groups.entries()].map(([id, g]) => {
    const first = g[0];
    const single = (field: (typeof identities)[number]) => {
      const values = new Set(g.map((r) => r[field]));
      return values.size === 1 ? g[0][field] : null;
    };
    const amounts = Object.fromEntries(numeric.map((field) => [field, sum(g, (r) => r[field])]));
    const unique = Object.fromEntries(identities.map((field) => [field, single(field)]));
    return {
      ...first,
      ...amounts,
      ...unique,
      key: id,
      sourceRows: g.length,
      sourceMark: new Set(g.map((row) => JSON.stringify(row.sourceMark))).size === 1 ? first.sourceMark : null,
      sourceEvidence: g.map((row) => ({ ...row.sourceMark })),
      displayName: first.displayName,
      displayProduct: productLabel(first.product_name),
      marginForecast:
        view === 'PCM'
          ? offerMarginForecast(first)
          : new Set(g.map(offerMarginForecast)).size === 1
            ? offerMarginForecast(first)
            : null,
      forecastMargin:
        view === 'PCM'
          ? offerMarginForecast(first)
          : new Set(g.map(offerMarginForecast)).size === 1
            ? offerMarginForecast(first)
            : null,
      refunds: sum(g, (r) => r.refunds_adjusted_paired),
      status: new Set(g.map((r) => r.status)).size === 1 ? first.status : 'Unknown',
      identityConflicts: [
        ...identities.filter((field) => new Set(g.map((r) => r[field])).size > 1),
        ...(new Set(g.map((r) => r.status)).size > 1 ? ['status'] : []),
      ],
    };
  });
}
export type Model = ReturnType<typeof build>;
export function validQuarter(value: string): boolean {
  return (
    value.slice(0, 4) !== '0000' &&
    /^\d{4}-(01|04|07|10)-01$/.test(value) &&
    !Number.isNaN(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}
